/**
 * Contacts — the "my ppl" contact management list.
 * Route: /contacts
 *
 * Redesigned with glass panels, MonoAvatar, alphabetical letter headers,
 * pill tabs, and gradient CTA matching the design reference.
 */

import React, { useEffect, useState, useMemo } from 'react';
import {
  Plus, Search, X, Star, UserPlus, Trash2,
  Check, ArrowLeft, ArrowRight, Loader,
} from 'react-feather';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import { getFeatherIcon } from '../utils/iconMap';
import SEO from '../components/SEO';
import MonoAvatar from '../components/MonoAvatar';
import {
  subscribeToContacts,
  addContact,
  toggleFavourite,
  deleteContact,
} from '../services/contactService';
import {
  RELATIONSHIP_OPTIONS,
  FREQUENCY_OPTIONS,
  INTENT_OPTIONS,
  MEETING_CONTEXT_OPTIONS,
  DEFAULT_CONTACT_FORM,
} from '../types/contacts';
import { LapseBanner, RememberMarker, SlotPill } from '../components/remember';
import useRememberList from '../hooks/useRememberList';
import { capture } from '../services/posthog';
import { MonetizationEvents, PaywallSource } from '../services/monetizationEvents';
import { rememberStateOf, windowPhrase } from '../services/rememberService';
import type { Contact, ContactFormData, RelationshipType, FrequencyLevel, IntentType, MeetingContext } from '../types/contacts';
import '../styles/contacts.css';
import '../styles/modern.css';

export default function Contacts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAuthenticated, redirectToLogin } = useAuthGate();
  const uid = user?.uid;

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'fav'>('all');
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState<ContactFormData>({ ...DEFAULT_CONTACT_FORM });
  const [wizardSaving, setWizardSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated && !loading) redirectToLogin();
  }, [isAuthenticated, loading, redirectToLogin]);

  // Real-time subscription
  useEffect(() => {
    if (!uid) return;
    setLoading(true);
    const unsub = subscribeToContacts(uid, (list) => {
      setContacts(list);
      setLoading(false);
    });
    return unsub;
  }, [uid]);

  // PPL-350 — the slot counter, the once-shown lapse banner, and the filter that
  // separates the ppl f8 remembers from the ppl it has paused. Counts come
  // from the FULL list, not the searched subset, so the chips stay put while
  // someone types.
  const rememberList = useRememberList(uid, contacts);

  // PPL-417 — the home desk's memory meter lands here on the bucket it is
  // about (`peopleFilter` in router state: 'active' | 'paused'). Applied once
  // the list is in — before that the filter's self-heal would reset it
  // against an empty page — and consumed, so a refresh starts from "all"
  // like any other visit.
  const location = useLocation();
  const { setFilter: setPeopleFilter } = rememberList;
  useEffect(() => {
    const wanted = (location.state as { peopleFilter?: unknown } | null)?.peopleFilter;
    if (loading || (wanted !== 'active' && wanted !== 'paused')) return;
    setPeopleFilter(wanted);
    navigate(location.pathname, { replace: true, state: null });
  }, [loading, location.state, location.pathname, navigate, setPeopleFilter]);

  // Filtered / searched contacts
  const displayed = useMemo(() => {
    let list = contacts;
    if (filter === 'fav') list = list.filter((c) => c.isFavourite);
    // Deliberately NOT reordered active-first the way the app's list is. This
    // one is an A–Z index, and hoisting a group above the alphabet would break
    // the thing people navigate it by. The chips below make the remembered set
    // readable as one instead — "remembering · 5" IS the inner circle.
    if (rememberList.filter !== 'all') {
      list = list.filter((c) => rememberStateOf(c) === rememberList.filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.firstName.toLowerCase().includes(q) ||
          c.lastName.toLowerCase().includes(q) ||
          (c.nickname || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [contacts, filter, rememberList.filter, search]);

  const favCount = useMemo(() => contacts.filter((c) => c.isFavourite).length, [contacts]);

  // Group by first letter
  const grouped = useMemo(() => {
    return displayed.reduce<Record<string, Contact[]>>((acc, c) => {
      const letter = (c.firstName[0] || '#').toUpperCase();
      (acc[letter] = acc[letter] || []).push(c);
      return acc;
    }, {});
  }, [displayed]);

  // ─── Wizard Handlers ──────────────────────────────────────────────────
  const openWizard = () => {
    setWizardData({ ...DEFAULT_CONTACT_FORM });
    setWizardStep(1);
    setShowWizard(true);
  };

  const closeWizard = () => {
    setShowWizard(false);
    setWizardStep(1);
    setWizardData({ ...DEFAULT_CONTACT_FORM });
  };

  const handleWizardSave = async () => {
    if (!uid || !wizardData.firstName.trim()) return;
    setWizardSaving(true);
    try {
      await addContact(uid, wizardData);
      closeWizard();
    } catch (err) {
      console.error('Failed to add contact:', err);
    } finally {
      setWizardSaving(false);
    }
  };

  const handleToggleFav = async (c: Contact) => {
    if (!uid) return;
    try {
      await toggleFavourite(uid, c.id, c.isFavourite);
    } catch {
      // optimistic update handled by real-time listener
    }
  };

  const handleDelete = async (personId: string) => {
    if (!uid) return;
    try {
      await deleteContact(uid, personId);
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  if (!isAuthenticated) return null;

  // Group relationships by group name
  const relGroups = RELATIONSHIP_OPTIONS.reduce<Record<string, typeof RELATIONSHIP_OPTIONS>>((acc, opt) => {
    if (!acc[opt.group]) acc[opt.group] = [];
    acc[opt.group].push(opt);
    return acc;
  }, {});

  const contactName = (c: Contact) =>
    `${c.firstName} ${c.lastName}`.trim();

  return (
    <>

      <div className="feed">
        <SEO title="my ppl | pplkpr" description="Manage your important people and relationships." path="/contacts" />

        <div className="contacts-shell">
          {/* Page header */}
          <div className="contacts-page-header">
            <div className="contacts-page-header__left">
              <h1 className="contacts-page-title">my ppl</h1>
              <span className="contacts-page-count">{contacts.length}</span>
              {/* PPL-350 — renders NOTHING on premium. A limit you cannot reach
                  is not reassurance; it is a standing reminder that a limit
                  exists, on the one plan without one. */}
              <SlotPill quota={rememberList.quota} small />
            </div>
            <button className="primary-button" onClick={openWizard}>
              <Plus size={14} />
              <span>add person</span>
            </button>
          </div>

          {/* Search + pill tabs */}
          <div className="contacts-controls">
            <div className="search-input-wrap search-input-wrap--full">
              <span className="search-input-wrap__icon">
                <Search size={14} />
              </span>
              <input
                className="search-input"
                type="text"
                placeholder="search contacts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="contacts-search-x" onClick={() => setSearch('')}>
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="pill-tabs">
              <button
                className={`pill-tab ${filter === 'all' ? 'pill-tab--active' : ''}`}
                onClick={() => setFilter('all')}
              >
                <span>all</span>
                <span className="pill-tab__count">{contacts.length}</span>
              </button>
              <button
                className={`pill-tab ${filter === 'fav' ? 'pill-tab--active' : ''}`}
                onClick={() => setFilter('fav')}
              >
                <Star size={13} />
                <span>favourites</span>
                <span className="pill-tab__count">{favCount}</span>
              </button>
            </div>
          </div>

          {/* PPL-350 — the lapse notice, once. It answers the contradiction a
              lapsed subscriber lands on (counter full, forty ppl) before that
              reads as the app having lost their data. */}
          {rememberList.lapsed ? (
            <LapseBanner
              quota={rememberList.quota}
              onRestart={() => {
                // The lapse banner is a paywall surface of its own — per the
                // design, the strongest re-subscribe moment after the at-limit
                // sheet (PPL-351).
                capture(MonetizationEvents.UPGRADE_CTA_CLICKED, {
                  source: PaywallSource.LAPSE_BANNER,
                  gate: null,
                });
                navigate(`/premium?source=${PaywallSource.LAPSE_BANNER}`);
              }}
              onChoose={() => { rememberList.setFilter('active'); void rememberList.dismissLapse(); }}
              onDismiss={() => { void rememberList.dismissLapse(); }}
            />
          ) : null}

          {/* Both populations reachable without a new page. Rendered whenever a
              filter is ON as well as when something is paused — a chip row that
              disappears while its filter is still applied strands the list with
              no control to undo it. */}
          {rememberList.counts.paused > 0 || rememberList.filter !== 'all' ? (
            <div className="pill-tabs" style={{ marginBottom: 16 }}>
              {([
                ['all', `all · ${rememberList.counts.all}`],
                ['active', `remembering · ${rememberList.counts.active}`],
                ['paused', `paused · ${rememberList.counts.paused}`],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  className={`pill-tab ${rememberList.filter === key ? 'pill-tab--active' : ''}`}
                  onClick={() => rememberList.setFilter(key)}
                >
                  <span>{label}</span>
                </button>
              ))}
            </div>
          ) : null}

          {/* PPL-350 — the state that carries the whole job of explaining the
              feature. Shown only when f8 is remembering nobody AND there is
              somebody to pick, so it never greets an empty address book with a
              pitch. No CTA: clicking any row reaches the control in one step, and
              a button that opened whoever happened to be first would be worse
              than none. */}
          {!loading
            && rememberList.quota
            && !rememberList.quota.isPremium
            && rememberList.quota.activeCount === 0
            && contacts.length > 0 ? (
            <div
              className="glass-panel"
              style={{
                padding: 16, marginBottom: 16,
                background: 'rgba(217,70,239,0.10)', border: '1px solid rgba(217,70,239,0.20)',
              }}
            >
              <div style={{ fontSize: 15.5, fontWeight: 700, color: '#fff', letterSpacing: '-0.015em' }}>
                f8 can remember the ppl who matter
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.55 }}>
                pick someone and f8 remembers their story — what you talked about, what&apos;s coming
                up, a brief before you meet.
                {/* Built as a clause LIST, not stacked conditionals: the window
                    can be served while the limit is not, and the naive version
                    rendered "…before you meet., the last 30 days" — a comma after
                    a full stop, with no terminator. */}
                {(() => {
                  const clauses = [
                    rememberList.quota.limit !== null ? `${rememberList.quota.limit} ppl` : null,
                    windowPhrase(rememberList.quota.memoryWindowDays),
                  ].filter(Boolean);
                  return clauses.length ? ` ${clauses.join(', ')}, free.` : '';
                })()}
              </div>
            </div>
          ) : null}

          {/* Contact list */}
          {loading ? (
            <div className="profile-loading" style={{ margin: '2rem 0' }}>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="skeleton-line wide" style={{ marginBottom: 12 }} />
              ))}
            </div>
          ) : displayed.length === 0 ? (
            <div className="contacts-empty-state">
              <UserPlus size={32} />
              <h3>{search ? 'no matches found' : filter === 'fav' ? 'no favourites yet' : "you haven't added anyone yet"}</h3>
              <p>{search ? 'try a different search term.' : 'add the important people in your life to stay connected.'}</p>
              {!search && filter !== 'fav' && (
                <button className="primary-button" onClick={openWizard}>
                  <Plus size={14} /> <span>add your first person</span>
                </button>
              )}
            </div>
          ) : (
            <div className="glass-panel contacts-panel">
              {Object.keys(grouped).sort().map((letter) => (
                <div key={letter}>
                  <div className="contacts-letter-header">{letter}</div>
                  {grouped[letter].map((c) => (
                    <div key={c.id} className="contacts-row">
                      <Link to={`/contacts/${c.id}`} className="contacts-row__main">
                        <MonoAvatar name={contactName(c)} size={36} />
                        <div className="contacts-row__info">
                          <span className="contacts-row__name">
                            {contactName(c)}
                            {c.nickname && <span className="contacts-row__nickname"> ({c.nickname})</span>}
                            {/* sparkle = f8 remembers, moon = paused, and
                                NOTHING for a person it was never asked to
                                remember. That third case is most of a synced
                                address book, and marking it would make the marker
                                wallpaper: the sparkle only means something because
                                most rows do not have one. */}
                            <span style={{ display: 'inline-flex', marginLeft: 7, verticalAlign: 'middle' }}>
                              <RememberMarker state={rememberStateOf(c)} size={13} />
                            </span>
                          </span>
                        </div>
                      </Link>
                      <button
                        className={`contacts-row__fav ${c.isFavourite ? 'contacts-row__fav--active' : ''}`}
                        onClick={() => handleToggleFav(c)}
                        title={c.isFavourite ? 'Unfavourite' : 'Favourite'}
                      >
                        <Star size={16} fill={c.isFavourite ? '#fde047' : 'none'} />
                      </button>
                      {deleteConfirm === c.id ? (
                        <div className="contacts-row__delete-confirm">
                          <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(c.id)} style={{ color: '#ff6b6b' }}>
                            delete
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setDeleteConfirm(null)}>
                            cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="contacts-row__remove"
                          onClick={() => setDeleteConfirm(c.id)}
                          title="Remove"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══════════ Add Contact Wizard Modal ══════════ */}
      {showWizard && (
        <div className="modal-overlay" onClick={closeWizard}>
          <div className="modal-content wizard-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>add a person — step {wizardStep} of 3</h3>
              <button className="modal-close" onClick={closeWizard}>
                <X size={18} />
              </button>
            </div>

            {/* Step indicators */}
            <div className="wizard-steps">
              {[1, 2, 3].map((s) => (
                <div key={s} className={`wizard-step-dot ${wizardStep === s ? 'active' : wizardStep > s ? 'done' : ''}`}>
                  {wizardStep > s ? <Check size={14} /> : s}
                </div>
              ))}
            </div>

            <div className="wizard-body">
              {/* Step 1: Basic Info */}
              {wizardStep === 1 && (
                <>
                  <p className="wizard-step-title">who would you like to add?</p>
                  <div className="wizard-field">
                    <label>first name *</label>
                    <input
                      type="text"
                      value={wizardData.firstName}
                      onChange={(e) => setWizardData({ ...wizardData, firstName: e.target.value })}
                      placeholder="first name"
                      autoFocus
                    />
                  </div>
                  <div className="wizard-field">
                    <label>last name</label>
                    <input
                      type="text"
                      value={wizardData.lastName}
                      onChange={(e) => setWizardData({ ...wizardData, lastName: e.target.value })}
                      placeholder="last name"
                    />
                  </div>
                  <div className="wizard-field">
                    <label>nickname</label>
                    <input
                      type="text"
                      value={wizardData.nickname}
                      onChange={(e) => setWizardData({ ...wizardData, nickname: e.target.value })}
                      placeholder="optional nickname"
                    />
                  </div>
                  <div className="wizard-row">
                    <div className="wizard-field">
                      <label>date of birth</label>
                      <input
                        type="date"
                        value={wizardData.dateOfBirth}
                        onChange={(e) => setWizardData({ ...wizardData, dateOfBirth: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="wizard-row">
                    <div className="wizard-field">
                      <label>phone</label>
                      <input
                        type="tel"
                        value={wizardData.phone}
                        onChange={(e) => setWizardData({ ...wizardData, phone: e.target.value })}
                        placeholder="phone number"
                      />
                    </div>
                    <div className="wizard-field">
                      <label>email</label>
                      <input
                        type="email"
                        value={wizardData.email}
                        onChange={(e) => setWizardData({ ...wizardData, email: e.target.value })}
                        placeholder="email address"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Step 2: Relationship & Context */}
              {wizardStep === 2 && (
                <>
                  <p className="wizard-step-title">How do you know {wizardData.firstName || 'them'}?</p>
                  <div className="wizard-field">
                    <label>relationship</label>
                    <select
                      value={wizardData.relationship}
                      onChange={(e) => setWizardData({ ...wizardData, relationship: e.target.value as RelationshipType })}
                    >
                      <option value="">select...</option>
                      {Object.entries(relGroups).map(([group, opts]) => (
                        <optgroup key={group} label={group}>
                          {opts.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div className="wizard-field">
                    <label>how did you meet?</label>
                    <select
                      value={wizardData.meetingContext}
                      onChange={(e) => setWizardData({ ...wizardData, meetingContext: e.target.value as MeetingContext })}
                    >
                      <option value="">select...</option>
                      {MEETING_CONTEXT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="wizard-field">
                    <label>notes</label>
                    <textarea
                      value={wizardData.notes}
                      onChange={(e) => setWizardData({ ...wizardData, notes: e.target.value })}
                      placeholder="anything you want to remember about this person..."
                      rows={3}
                    />
                  </div>
                </>
              )}

              {/* Step 3: Engagement */}
              {wizardStep === 3 && (
                <>
                  <p className="wizard-step-title">How often do you want to connect?</p>
                  <div className="wizard-field">
                    <label>contact frequency</label>
                    <div className="wizard-options-grid">
                      {FREQUENCY_OPTIONS.map((f) => (
                        <button
                          key={f.value}
                          className={`wizard-option-btn ${wizardData.frequency === f.value ? 'active' : ''}`}
                          onClick={() => setWizardData({ ...wizardData, frequency: f.value as FrequencyLevel })}
                        >
                          <span className="wizard-option-label">{f.label}</span>
                          <span className="wizard-option-desc">{f.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="wizard-field">
                    <label>intent</label>
                    <div className="wizard-options-grid compact">
                      {INTENT_OPTIONS.map((i) => (
                        <button
                          key={i.value}
                          className={`wizard-option-btn ${wizardData.intent === i.value ? 'active' : ''}`}
                          onClick={() => setWizardData({ ...wizardData, intent: i.value as IntentType })}
                        >
                          {(() => { const Icon = getFeatherIcon(i.icon); return <Icon size={16} />; })()}
                          <span className="wizard-option-label">{i.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="wizard-field">
                    <label className="wizard-checkbox">
                      <input
                        type="checkbox"
                        checked={wizardData.isFavourite}
                        onChange={(e) => setWizardData({ ...wizardData, isFavourite: e.target.checked })}
                      />
                      <Star size={16} style={{ color: wizardData.isFavourite ? '#FFD700' : 'rgba(255,255,255,0.3)' }} fill={wizardData.isFavourite ? '#FFD700' : 'none'} />
                      mark as favourite
                    </label>
                  </div>
                </>
              )}
            </div>

            {/* Wizard navigation */}
            <div className="wizard-nav">
              {wizardStep > 1 && (
                <button className="btn btn-secondary" onClick={() => setWizardStep(wizardStep - 1)}>
                  <ArrowLeft size={14} /> back
                </button>
              )}
              <div className="wizard-nav-spacer" />
              {wizardStep < 3 ? (
                <button
                  className="btn btn-primary"
                  onClick={() => setWizardStep(wizardStep + 1)}
                  disabled={wizardStep === 1 && !wizardData.firstName.trim()}
                >
                  next <ArrowRight size={14} />
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={handleWizardSave}
                  disabled={wizardSaving || !wizardData.firstName.trim()}
                >
                  {wizardSaving ? (
                    <><Loader size={14} className="spin" /> saving...</>
                  ) : (
                    <><Check size={14} /> save</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
